// Pipeline v2: list of EmailIssue rows extracted from an artifact.
//
// Shown under the email body in the artifact viewer + workspace right
// panel. An email can produce 0, 1, or many issues (v2 detect_email_issues
// step). Each row shows the issue description, filing decision, the matter
// it landed in (if filed), and any obligations attached to it.

import { useEffect, useState } from 'react'
import { Loader2, Triangle, FileText, Clock, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { emailIssues, issueMatchAssessments } from '../api'
import type { EmailIssue, IssueMatchAssessment } from '../api'

type Props = {
  artifactId: number
  artifactType?: string  // 'email' | 'document' | 'attachment'
}

const FILING_BADGE: Record<string, { label: string; color: string }> = {
  file_issue_with_existing_matter: { label: 'Filed (existing)', color: 'var(--me-fresh, #2a9d8f)' },
  create_new: { label: 'Filed (new matter)', color: 'var(--me-info, #38b6ff)' },
  '': { label: 'Pending decision', color: 'var(--me-mute, #888)' },
}

export function EmailIssueList({ artifactId, artifactType }: Props) {
  const [issues, setIssues] = useState<EmailIssue[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Only emails have issues. Skip the fetch for attachments/documents.
    if (artifactType && artifactType !== 'email') {
      setIssues([])
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    emailIssues.byArtifact(artifactId)
      .then((data) => {
        if (!cancelled) setIssues(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load issues.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [artifactId, artifactType])

  if (artifactType && artifactType !== 'email') return null

  return (
    <div className="me-card p-3 mt-2">
      <div className="flex items-center gap-1.5 text-xs me-text-mute mb-2">
        <Triangle size={11} />
        <span>Issues extracted by pipeline v2</span>
        {loading && <Loader2 size={11} className="animate-spin ml-1" />}
      </div>

      {error && (
        <div className="text-[11px] text-red-500">{error}</div>
      )}

      {!loading && !error && issues !== null && issues.length === 0 && (
        <div className="text-[11px] me-text-dim italic">
          No issues extracted yet. Run the v2 <code>summarize_artifact</code> prompt via
          Studio → Test Run to populate.
        </div>
      )}

      {issues && issues.length > 0 && (
        <ul className="space-y-2">
          {issues.map((issue) => (
            <IssueRow key={issue.id} issue={issue} />
          ))}
        </ul>
      )}
    </div>
  )
}


function IssueRow({ issue }: { issue: EmailIssue }) {
  const badge = FILING_BADGE[issue.filing_decision] ?? FILING_BADGE['']
  const [showWhy, setShowWhy] = useState(false)
  const [assessments, setAssessments] = useState<IssueMatchAssessment[] | null>(null)
  const [loadingWhy, setLoadingWhy] = useState(false)

  const toggleWhy = async () => {
    const next = !showWhy
    setShowWhy(next)
    if (next && assessments === null && !loadingWhy) {
      setLoadingWhy(true)
      try {
        const data = await issueMatchAssessments.byIssue(issue.id)
        setAssessments(data)
      } catch {
        setAssessments([])
      } finally {
        setLoadingWhy(false)
      }
    }
  }

  return (
    <li className="border-l-2 pl-2" style={{ borderColor: badge.color }}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs me-text leading-snug flex-1">
          <span className="me-mono me-text-mute mr-1">#{issue.issue_index}</span>
          {issue.description || <em className="me-text-dim">(no description)</em>}
        </div>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded shrink-0"
          style={{ background: 'rgba(0,0,0,0.05)', color: badge.color }}
          title={issue.filing_reasoning || ''}
        >
          {badge.label}
        </span>
      </div>

      {issue.matter_name && (
        <div className="text-[11px] me-text-dim mt-0.5 flex items-center gap-1">
          <FileText size={10} />
          → {issue.matter_name}
          {issue.filing_confidence != null && (
            <span className="me-mono ml-1">
              ({(issue.filing_confidence * 100).toFixed(0)}%)
            </span>
          )}
          <button
            onClick={toggleWhy}
            className="ml-1 text-[10px] me-text-mute hover:me-text underline-offset-2 hover:underline flex items-center"
            title="Show all per-matter assessments for this issue"
          >
            {showWhy ? <ChevronDown size={10} /> : <ChevronRight size={10} />} why
          </button>
        </div>
      )}

      {issue.summary && (
        <div className="text-[11px] me-text-dim mt-0.5 italic">{issue.summary}</div>
      )}

      {showWhy && (
        <div className="mt-1 pl-3 border-l border-[color:var(--me-border)] text-[10.5px] me-text-dim">
          {loadingWhy && <Loader2 size={10} className="animate-spin" />}
          {!loadingWhy && assessments && assessments.length === 0 && (
            <span className="italic">No match assessments recorded for this issue.</span>
          )}
          {!loadingWhy && assessments && assessments.length > 0 && (
            <ul className="space-y-0.5">
              {assessments.slice(0, 10).map((a) => (
                <li key={a.id} className="flex items-baseline gap-1">
                  <span className="me-mono shrink-0">
                    {(a.confidence * 100).toFixed(0).padStart(3, ' ')}%
                  </span>
                  <span className={a.does_belong ? 'me-text' : 'me-text-mute'}>
                    {a.matter_name || `M#${a.matter}`}
                  </span>
                  {a.reason && <span className="me-text-mute italic"> — {a.reason}</span>}
                </li>
              ))}
              {assessments.length > 10 && (
                <li className="me-text-mute italic">… +{assessments.length - 10} more</li>
              )}
            </ul>
          )}
        </div>
      )}

      {issue.obligations?.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {issue.obligations.map((ob, i) => (
            <li key={i} className="text-[11px] me-text-dim flex items-start gap-1">
              <AlertCircle size={10} className="mt-0.5 shrink-0" />
              <span>
                <span className="me-mono me-text-mute mr-1">{ob.direction}</span>
                {ob.description}
                {ob.due_date && (
                  <span className="ml-1 me-text-mute">
                    <Clock size={9} className="inline" /> {ob.due_date}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
